import React, { useEffect, useState } from 'react';

function isActiveElementIframe() {
  return (
    !!document.activeElement && document.activeElement.tagName === 'IFRAME'
  );
}

interface IContext {
  isWindowFocused: boolean;
}

export const WindowFocusContext: React.Context<IContext> =
  React.createContext<IContext>({
    isWindowFocused: document.hasFocus(),
  });

type Props = React.PropsWithChildren<unknown>;

// This context makes the browser window/tab focus reactive
// We didn't implement it as a hook to avoid binding multiple window listeners
const WindowFocusContextProvider: React.FC<Props> = (
  props: Props
): JSX.Element => {
  const { children } = props;

  /**
   * Read the initial window/tab focus
   *
   * Keep in mind the window/tab can be open in the background
   * or the user can jump away the tab while it loads
   *
   * @warning `document.visibilityState` and `document.hidden` are not reliable
   *          because the content can be visible in the background tab.
   *
   * @todo detect mouseover/scrolling even if the window is not focus
   */
  const [focus, setFocus] = useState(document.hasFocus());

  /**
   * React to the browser native way of detecting focus state
   *
   * Opening a new tab will trigger the 'visibilityChange' event but not
   * always the blur event. Focusing on another window while the one
   * with the active tab is still on the screen won't trigger a
   * visibilityChange event, but it will trigger a blur event.
   */
  useEffect(() => {
    /**
     * Handler for the window's blur event. This is triggered when the window
     * loses focus because the user changed the active tab, clicked on another
     * app in their computers or switched windows. This is not the same as
     * document.visibilityState = 'hidden' because if the window is still
     * visible within the user's screen but has lost focus, it will trigger
     * the blur event, but the document's visibility state will remain visible.
     * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/blur_event
     */
    const onBlur = () => {
      /**
       * Blur event is also triggered iframes:
       * - Fullscreen URL
       * - Embeddable content
       * - PSPDFKit
       * We shouldn't lose focus due to those blur events
       */
      if (isActiveElementIframe()) {
        return;
      }
      setFocus(false);
    };

    /**
     * Handler for the window's focus event. This is triggered when an element
     * within the document has gained focus. This focus could be triggerd by
     * just clicking on an empty space on the screen, but may not work
     * if the tab (on the browser's top navigation) is clicked.
     * @note This doesn't guarantee that the user has gained interest on the tab
     * and is active anymore, in combination with visibilityChange could
     * give a more accurate description when a user comes back to the tab.
     * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/focus_event
     */
    const onFocus = () => setFocus(true);

    /**
     * Checks if the browser's tab has lost focus. This listener will be
     * triggered if the tab or window has left the viewport, but won't be
     * triggered if the tab has lost focus but it still is within the screen.
     * @see https://www.w3.org/TR/page-visibility/#dfn-hidden
     */
    const onVisibilityChange = () =>
      setFocus(document.visibilityState === 'visible' && document.hasFocus());

    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);
    window.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  const context = { isWindowFocused: focus };

  return (
    <WindowFocusContext.Provider value={context}>
      {children}
    </WindowFocusContext.Provider>
  );
};

export default WindowFocusContextProvider;
